import React, { useEffect, useRef } from 'react';
import './MediaPlaceholder.css';

export type MediaPlaceholderProps = {
  /** What this image will be replaced with — shown on the placeholder */
  label: string;
  className?: string;
};

/**
 * Dashed frame + caption for photos not yet provided. Use instead of &lt;img&gt; until assets are ready.
 */
export const MediaPlaceholder: React.FC<MediaPlaceholderProps> = ({ label, className = '' }) => (
  <div
    className={`ln-media-placeholder ${className}`.trim()}
    role="img"
    aria-label={label}
  >
    <span className="ln-media-placeholder-inner">
      <span className="ln-media-placeholder-badge">Image placeholder</span>
      <span className="ln-media-placeholder-label">{label}</span>
    </span>
  </div>
);

export type VideoPlaceholderProps = {
  label: string;
  className?: string;
  /** When set, renders a looping muted ambient clip (same layout hooks as the dashed placeholder). */
  src?: string;
  /** Slows playback for calm ambient backgrounds (default 0.82). Set to 1 to disable. */
  playbackRate?: number;
};

/**
 * Hero / background slot: optional MP4, otherwise dashed placeholder until a file is wired.
 */
export const VideoPlaceholder: React.FC<VideoPlaceholderProps> = ({
  label,
  className = '',
  src,
  playbackRate = 0.82,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !src) return;
    el.playbackRate = playbackRate;
  }, [src, playbackRate]);

  if (src) {
    return (
      <video
        ref={videoRef}
        className={className.trim()}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label={label}
      />
    );
  }
  return (
    <div
      className={`ln-video-placeholder ${className}`.trim()}
      role="img"
      aria-label={label}
    >
      <div className="ln-video-placeholder-inner">
        <span className="ln-video-placeholder-icon" aria-hidden>
          ▶
        </span>
        <p className="ln-video-placeholder-text">{label}</p>
      </div>
    </div>
  );
};
